import { resolveRafikiAccess, isRafikiFeature } from './rafikiAccessPolicy';
import { PRODUCT_CATALOG_VERSION, PRODUCT_FEATURE_BY_ID, PRODUCT_OFFER_PRESETS, resolveProductFeatures } from './productFeatureCatalog';

const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const record = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
// Only this Firestore root field is staff-protected. Redux adapters unwrap their
// own slice before calling this policy; arbitrary nested document data is inert.
export const getProductConfiguration = (zoo) => zoo?.productConfiguration ?? null;

const featureList = (value) => Array.isArray(value) && value.every((id) => typeof id === 'string');
const knownAdditions = (additions) => Object.fromEntries(Object.entries(additions)
  .filter(([id, enabled]) => own(PRODUCT_FEATURE_BY_ID, id) && enabled === true));

export function normalizeProductConfiguration(value) {
  if (!record(value) || (value.features !== undefined && !featureList(value.features))) return null;
  // The enabled list written at save time is the real grant: this client needs
  // no knowledge of the preset name or catalogue version to read it.
  if (featureList(value.features)) {
    if (!Number.isInteger(value.version) || value.version < 1
      || !(value.tier === null || typeof value.tier === 'string')
      || !Array.isArray(value.programs) || value.programs.some((id) => typeof id !== 'string')
      || !(value.additions === undefined || record(value.additions))) return null;
    return {
      version: value.version,
      tier: value.tier,
      programs: [...new Set(value.programs)],
      additions: knownAdditions(value.additions || {}),
      features: [...new Set(value.features.filter((id) => own(PRODUCT_FEATURE_BY_ID, id)))],
    };
  }
  if (value.version !== PRODUCT_CATALOG_VERSION
    || !(value.tier === null || own(PRODUCT_OFFER_PRESETS, value.tier))
    || !Array.isArray(value.programs) || value.programs.some((id) => id !== 'logbook')
    || !record(value.additions)) return null;
  return {
    version: PRODUCT_CATALOG_VERSION,
    tier: value.tier,
    programs: [...new Set(value.programs)],
    additions: knownAdditions(value.additions),
  };
}

/** Park capabilities only. User rights, operational settings and privacy remain independent. */
export function resolveZooProductScope(zoo, userId = zoo?.productScopeUserId) {
  const raw = getProductConfiguration(zoo);
  const configuration = normalizeProductConfiguration(raw);
  const mode = raw == null ? 'legacy' : configuration ? 'configured' : 'invalid';
  const resolved = resolveProductFeatures(configuration || {});
  const rafiki = resolveRafikiAccess(zoo, userId);
  // An explicit Rafiki configuration only restricts an offered feature; it never grants one outside the offer.
  const can = (id) => own(PRODUCT_FEATURE_BY_ID, id) && (mode === 'legacy' || resolved.can(id))
    && (!isRafikiFeature(id) || rafiki.mode === 'legacy' || rafiki.allowed);
  return {
    ...resolved, mode, configuration, rafiki,
    can,
    allows: (ids) => !ids?.length || ids.some(can),
  };
}
